import { Loading, Typography } from "@/components";
import { BasicButton } from "@/components/button";
import { BackAppBar } from "@/components/ui";
import useTeamMutation from "@/hooks/useTeamMutation";
import { cn } from "@/libraries/cn";
import type { CreateTeamFormSchema } from "@/schemas";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import {
	Controller,
	type SubmitHandler,
	useFormContext,
} from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { toast } from "sonner";

export const Route = createFileRoute("/dashboard/teams/create/name")({
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const isDirectory = Route.useSearch({
		select: (s) => s.directory,
	});
	const { handleSubmit, control, watch, formState } =
		useFormContext<CreateTeamFormSchema>();
	const selectedUserIds = watch("user_ids", new Set([]));
	const teamName = watch("name", "");

	const { create } = useTeamMutation();

	const onSubmit = useCallback<SubmitHandler<CreateTeamFormSchema>>(
		async (data, e) => {
			e?.preventDefault();
			try {
				const newteam = await create.mutateAsync({
					...data,
					without_members_response: true,
				});
				return navigate({
					replace: true,
					to: "/dashboard/teams/$id",
					params: {
						id: newteam.team.id,
					},
				});
			} catch (error) {
				return toast.error("Failed to create new team");
			}
		},
		[create, navigate],
	);

	useEffect(() => {
		if (!selectedUserIds.size) {
			navigate({
				replace: true,
				to: "/dashboard/teams/create",
				search: {
					directory: isDirectory,
				},
			});
		}
	}, [navigate, selectedUserIds, isDirectory]);

	return (
		<div className="h-full flex flex-col overflow-hidden">
			<BackAppBar title="Add Team" subTitle="Add Name" />
			<form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col">
				<div className="relative container flex-1 flex flex-col">
					<div className="flex flex-1 flex-col">
						<div className="container py-4">
							<TextFieldElement
								fullWidth
								control={control}
								label="Team Name"
								name="name"
								className="[&_.MuiInputBase-root]:!rounded-xl"
								error={!!formState.errors.name}
								helperText={
									<span className="flex flex-row justify-between items-center gap-2">
										<span className="flex-1">
											{formState.errors.name?.message ? (
												<Typography component="span" color="error">
													{formState.errors.name?.message}
												</Typography>
											) : null}
										</span>
										<Typography component="span">{`${teamName.length}/100`}</Typography>
									</span>
								}
							/>
						</div>
						<Divider />
						<div className="container py-4">
							<div className="flex flex-col gap-4">
								<Typography fontWeight={700} fontSize={14}>
									Add to HEMI Teams Directory
								</Typography>
								<Controller
									control={control}
									name="directory"
									defaultValue={false}
									render={({ field }) => (
										<>
											<Stack
												direction="row"
												spacing={2}
												justifyContent="center"
												alignItems="center"
											>
												<BasicButton
													variant="outlined"
													color={field.value === true ? "info" : "inherit"}
													className={cn(
														"w-full !h-12 !text-base font-medium !shadow-none",
														{
															"!bg-primary-light": field.value,
														},
													)}
													onClick={() => field.onChange(true)}
													onBlur={field.onBlur}
												>
													<Box
														component="span"
														className="flex items-center gap-2"
													>
														<CheckIcon />
														<span>Yes</span>
													</Box>
												</BasicButton>
												<BasicButton
													variant="outlined"
													color={field.value === false ? "error" : "inherit"}
													className={cn(
														"w-full !h-12 !text-base font-medium !shadow-none",
														{
															"!bg-danger-100": !field.value,
														},
													)}
													onClick={() => field.onChange(false)}
													onBlur={field.onBlur}
												>
													<Box
														component="span"
														className="flex items-center gap-2"
													>
														<CloseIcon />
														<span>No</span>
													</Box>
												</BasicButton>
											</Stack>
											<Box className="flex items-start gap-1">
												<InfoRoundedIcon className="!text-base" />
												<p className="text-dark text-xs font-medium">
													{field.value
														? "Your Team profile has been added to HEMI Teams Directory so others may contact your Team directly."
														: "Your Team profile has not been added to the directory and other users who are not in your phone contact will not be able to message your Team."}
												</p>
											</Box>
										</>
									)}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className="container py-4 shadow flex flex-row gap-4 justify-center items-center">
					<BasicButton
						type="submit"
						variant="contained"
						color="secondary"
						className="w-full !h-12 text-sm font-semibold !shadow-none"
						disable={!formState.isValid || formState.isSubmitting}
					>
						Create Team
					</BasicButton>
				</div>
			</form>
			<Loading status={create.isPending} />
		</div>
	);
}
